import { useCallback, useEffect, useRef, useState } from 'react';
import {
  addDoc,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  increment,
  onSnapshot,
  query,
  runTransaction,
  Timestamp,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { AnalyticsEvents, trackEvent } from '../analytics/events';
import { useListenerLatch } from './useListenerLatch';
import {
  deckDoc,
  deckLikesCollection,
  deckVersionsCollection,
  decksCollection,
} from '../firebase/collections';
import type { Deck, DeckDraft, DeckVisibility, ValidationResult } from '../types/deck';
import {
  deckLimitReachedMessage,
  isAtDeckLimit,
  USER_LIMITS,
} from '../constants/userLimits';
import {
  clearConsideredForRemix,
  emptyDeckDraft,
  sanitizeDraftForViewer,
} from '../types/deck';
import { deckDraftUnchanged } from '../utils/deckDiff';
import { DeckPublishedDuringSave, draftForClientPersist } from '../utils/deckPersist';
import { normalizeMetafyArticle } from '../utils/metafyArticle';
import { validateDeck } from '../utils/deckValidation';
import { compareDecksByGallerySort, DEFAULT_DECK_GALLERY_SORT } from '../utils/deckGalleryFilter';
import { clearFeaturedPointersForDeck } from '../utils/featuredDeck';
import { renameSignedPrintCard, renameSignedPrintCards } from '../utils/deckCardRef';
import { useAuth } from './useAuth';

// ---------- helpers ----------

function draftFromFirestore(data: Record<string, unknown>): DeckDraft {
  return {
    title: (data.title as string) || '',
    description: (data.description as string) || '',
    guide: (data.guide as string) || '',
    video: (data.video as DeckDraft['video']) || undefined,
    metafy: normalizeMetafyArticle(data.metafy),
    playstyle: (data.playstyle as DeckDraft['playstyle']) || [],
    formatCode: (data.formatCode as DeckDraft['formatCode']) || 'UNL',
    tags: (data.tags as string[]) || [],
    coverCardId: (data.coverCardId as string) || undefined,
    matchups: (data.matchups as DeckDraft['matchups']) || [],
    legend: renameSignedPrintCard(data.legend as DeckDraft['legend']) || undefined,
    chosenLegends: renameSignedPrintCards(data.chosenLegends as DeckDraft['chosenLegends']),
    champion: renameSignedPrintCard(data.champion as DeckDraft['champion']) || undefined,
    battlefields: renameSignedPrintCards(data.battlefields as DeckDraft['battlefields']),
    runes: (data.runes as DeckDraft['runes']) || [],
    mainDeck: renameSignedPrintCards(data.mainDeck as DeckDraft['mainDeck']),
    sideboard: renameSignedPrintCards(data.sideboard as DeckDraft['sideboard']),
    considered: renameSignedPrintCards(data.considered as DeckDraft['considered']),
    status: (data.status as DeckDraft['status']) || 'draft',
    visibility: (data.visibility as DeckDraft['visibility']) || 'public',
  };
}

/** Load a deck snapshot for remix — does not write to Firestore. */
export async function fetchRemixDraft(sourceDeckId: string): Promise<DeckDraft> {
  const snap = await getDoc(deckDoc(sourceDeckId));
  if (!snap.exists()) throw new Error('Source deck not found.');

  const draft = clearConsideredForRemix(draftFromFirestore(snap.data() as Record<string, unknown>));
  draft.title = `Remix of ${draft.title || 'Untitled'}`;
  draft.status = 'draft';
  draft.visibility = 'public';
  return draft;
}

function deckFromFirestore(
  id: string,
  data: Record<string, unknown>,
  opts?: { isOwner?: boolean },
): Deck {
  const draft = sanitizeDraftForViewer(draftFromFirestore(data), {
    isOwner: opts?.isOwner ?? false,
  });
  return {
    ...draft,
    id,
    ownerId: (data.ownerId as string) || '',
    ownerDisplayName: (data.ownerDisplayName as string) || '',
    ownerHandle: (data.ownerHandle as string) || '',
    ownerAvatarUrl: typeof data.ownerAvatarUrl === 'string' ? data.ownerAvatarUrl : null,
    teamId: typeof data.teamId === 'string' ? data.teamId : null,
    isValid: (data.isValid as boolean) ?? false,
    validationErrors: (data.validationErrors as string[]) || [],
    likeCount: (data.likeCount as number) || 0,
    viewCount: (data.viewCount as number) || 0,
    currentVersion: (data.currentVersion as number) || 0,
    createdAt: (data.createdAt as Timestamp)?.toMillis?.() || Date.now(),
    updatedAt: (data.updatedAt as Timestamp)?.toMillis?.() || Date.now(),
    tournamentResult: (data.tournamentResult as Deck['tournamentResult']) || undefined,
  };
}

function teamFieldsForSave(
  draft: DeckDraft,
  teamId: string | null | undefined
): { teamId: string | null } {
  // Team-only and public decks both appear on the team profile when the owner
  // is on a team. Private decks never carry a teamId.
  if ((draft.visibility === 'team' || draft.visibility === 'public') && teamId) {
    return { teamId };
  }
  return { teamId: null };
}

// ---------- remembered decks (gallery → detail → edit) ----------

/** Last deck object seen for an id. Lets the next screen paint on the click frame. */
const rememberedDeckCache = new Map<string, Deck>();

export function rememberDeck(deck: Deck): void {
  rememberedDeckCache.set(deck.id, deck);
}

export function rememberDecks(decks: readonly Deck[]): void {
  for (const deck of decks) rememberedDeckCache.set(deck.id, deck);
}

export function getRememberedDeck(deckId: string | undefined): Deck | null {
  if (!deckId) return null;
  return rememberedDeckCache.get(deckId) ?? null;
}

// ---------- hook: single deck (real-time) ----------

export function useDeck(deckId: string | undefined): {
  deck: Deck | null;
  loading: boolean;
  error: string | null;
} {
  const { user } = useAuth();
  const [deck, setDeck] = useState<Deck | null>(() => getRememberedDeck(deckId));
  // Stay "loading" until the live snapshot, even when a remembered deck can paint.
  // Callers that already have `deck` should render it and skip the skeleton.
  const [loading, setLoading] = useState(Boolean(deckId));
  const [error, setError] = useState<string | null>(null);
  const trackedDeckId = useRef(deckId);

  if (trackedDeckId.current !== deckId) {
    trackedDeckId.current = deckId;
    setDeck(getRememberedDeck(deckId));
    setLoading(Boolean(deckId));
    setError(null);
  }

  useEffect(() => {
    if (!deckId) {
      setDeck(null);
      setLoading(false);
      return;
    }
    const unsub = onSnapshot(
      deckDoc(deckId),
      (snap) => {
        if (!snap.exists()) {
          setDeck(null);
          setError('Deck not found.');
        } else {
          const data = snap.data() as Record<string, unknown>;
          const ownerId = (data.ownerId as string) || '';
          const next = deckFromFirestore(snap.id, data, {
            isOwner: Boolean(user?.uid && user.uid === ownerId),
          });
          rememberDeck(next);
          setDeck(next);
          setError(null);
        }
        setLoading(false);
      },
      (err) => {
        console.error('[useDeck] snapshot error', err);
        setError(err.message);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [deckId, user?.uid]);

  return { deck, loading, error };
}

// ---------- hook: user's own decks list ----------

/** Session cache so remounts / Activity re-shows don't empty-flash. */
const userDecksCache = new Map<string, Deck[]>();

export function useUserDecks(options?: { enabled?: boolean }): {
  decks: Deck[];
  loading: boolean;
  error: string | null;
} {
  const { user } = useAuth();
  const uid = user?.uid ?? null;
  const slug = user?.slug;
  const enabled = options?.enabled !== false;
  const keepAlive = useListenerLatch(uid, enabled);
  const cached = uid ? userDecksCache.get(uid) : undefined;
  const [decks, setDecks] = useState<Deck[]>(() => cached ?? []);
  const [loading, setLoading] = useState(() => Boolean(uid) && cached === undefined && enabled);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!uid) {
      setDecks([]);
      setLoading(false);
      return;
    }
    const warm = userDecksCache.get(uid);
    if (warm) {
      setDecks(warm);
      setLoading(false);
    }
    if (!keepAlive) {
      if (!warm) setLoading(false);
      return;
    }
    if (!warm) setLoading(true);
    const q = query(decksCollection(), where('ownerId', '==', uid));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const result: Deck[] = snap.docs.map((d) => {
          const deck = deckFromFirestore(d.id, d.data() as Record<string, unknown>, {
            isOwner: true,
          });
          if (!deck.ownerHandle && slug) {
            deck.ownerHandle = slug;
          }
          return deck;
        });
        result.sort((a, b) => b.updatedAt - a.updatedAt);
        userDecksCache.set(uid, result);
        rememberDecks(result);
        setDecks(result);
        setLoading(false);
      },
      (err) => {
        console.error('[useUserDecks] snapshot error', err);
        setError(err.message);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [uid, keepAlive, slug]);

  return { decks, loading, error };
}

// ---------- hook: deck mutations ----------

export function useDeckMutations() {
  const { user, getIdToken } = useAuth();

  const createDeck = useCallback(
    async (
      initial?: Partial<DeckDraft>,
      opts?: { analytics?: 'create' | 'duplicate' },
    ): Promise<string> => {
      if (!user) throw new Error('Must be signed in to create a deck.');

      const existing = await getDocs(
        query(decksCollection(), where('ownerId', '==', user.uid))
      );
      if (isAtDeckLimit(existing.size)) {
        throw new Error(deckLimitReachedMessage());
      }

      const draft: DeckDraft = { ...emptyDeckDraft(), ...initial };
      if (draft.status === 'published') {
        // Publishing is server-authoritative; new documents always start as drafts.
        draft.status = 'draft';
      }
      const validation: ValidationResult = validateDeck(draft);
      const now = Timestamp.now();

      const ref = await addDoc(decksCollection(), {
        ...serializeDraft(draft),
        ...teamFieldsForSave(draft, user.teamId),
        ownerId: user.uid,
        ownerDisplayName: user.displayName || user.email || 'Anonymous',
        ownerHandle: user.slug || '',
        ownerAvatarUrl: user.avatarUrl ?? user.photoURL ?? null,
        isValid: validation.isValid,
        validationErrors: validation.errors,
        likeCount: 0,
        viewCount: 0,
        currentVersion: 0,
        createdAt: now,
        updatedAt: now,
      });
      const analytics = opts?.analytics ?? 'create';
      if (analytics === 'duplicate') {
        trackEvent(AnalyticsEvents.DECK_DUPLICATE);
      } else {
        trackEvent(AnalyticsEvents.DECK_CREATE);
      }
      return ref.id;
    },
    [user]
  );

  const publishDeck = useCallback(
    async (
      deckId: string,
      draft: DeckDraft,
      opts?: { bumpVersion?: boolean; changeNote?: string },
    ): Promise<{ saved: boolean }> => {
      if (!user) throw new Error('Must be signed in.');

      const token = await getIdToken();
      if (!token) throw new Error('Your session expired. Sign in again to publish.');

      const nextVisibility: DeckVisibility =
        draft.visibility === 'team' && !user.teamId ? 'private' : draft.visibility;
      const publishDraft: DeckDraft = {
        ...draft,
        status: 'published',
        visibility: nextVisibility,
      };
      const validation = validateDeck(publishDraft);
      if (!validation.isValid) {
        throw new Error(validation.errors[0] || 'Deck must be legal before publishing.');
      }

      const deckSnap = await getDoc(deckDoc(deckId));
      if (!deckSnap.exists()) throw new Error('Deck not found.');

      const response = await fetch('/api/decks-publish', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          deckId,
          visibility: nextVisibility,
          draft: publishDraft,
          bumpVersion: opts?.bumpVersion === true,
          changeNote: opts?.changeNote || '',
        }),
      });

      if (!response.ok) {
        let message = 'Could not publish deck.';
        try {
          const body = await response.json();
          if (Array.isArray(body?.validationErrors) && body.validationErrors.length) {
            message = body.validationErrors[0];
          } else if (typeof body?.error === 'string') {
            message = body.error;
          }
        } catch {
          /* keep generic message */
        }
        throw new Error(message);
      }

      const prevData = deckSnap.data() as Record<string, unknown>;
      await clearFeaturedPointersForDeck({
        deckId,
        ownerHandle: typeof prevData.ownerHandle === 'string' ? prevData.ownerHandle : user.slug,
        teamId: typeof prevData.teamId === 'string' ? prevData.teamId : user.teamId,
        nextVisibility,
      });

      return { saved: true };
    },
    [user, getIdToken],
  );

  /**
   * Persist deck fields to Firestore.
   * When `bumpVersion` is false (autosave), updates the deck doc only — no versions
   * subdoc and no `currentVersion` bump (avoids burning the rolling version cap).
   */
  const persistDeckDraft = useCallback(
    async (
      deckId: string,
      draft: DeckDraft,
      opts: { bumpVersion: boolean; changeNote?: string; force?: boolean },
    ): Promise<{ saved: boolean }> => {
      if (!user) throw new Error('Must be signed in.');

      const snap = await getDoc(deckDoc(deckId));
      if (!snap.exists()) throw new Error('Deck not found.');

      const existingData = snap.data() as Record<string, unknown>;
      const existingDraft = draftFromFirestore(existingData);
      const toPersist = draftForClientPersist(existingDraft.status, draft);
      if (!opts.force && deckDraftUnchanged(existingDraft, toPersist)) {
        return { saved: false };
      }

      if (toPersist.status === 'published') {
        return publishDeck(deckId, toPersist, {
          bumpVersion: opts.bumpVersion,
          changeNote: opts.changeNote,
        });
      }

      const validation = validateDeck(toPersist);
      const now = Timestamp.now();
      const bumpVersion = opts.bumpVersion;
      const deckRef = deckDoc(deckId);
      let nextVersion = (existingData.currentVersion as number) || 0;
      let prevTeamId: unknown = existingData.teamId;

      try {
        await runTransaction(db, async (tx) => {
          const fresh = await tx.get(deckRef);
          if (!fresh.exists()) throw new Error('Deck not found.');
          const freshData = fresh.data() as Record<string, unknown>;
          if (freshData.status === 'published') {
            throw new DeckPublishedDuringSave();
          }

          const currentVersion = (freshData.currentVersion as number) || 0;
          nextVersion = bumpVersion ? currentVersion + 1 : currentVersion;
          prevTeamId = freshData.teamId;

          tx.update(deckRef, {
            ...serializeDraft(toPersist),
            ...teamFieldsForSave(toPersist, user.teamId),
            isValid: validation.isValid,
            validationErrors: validation.errors,
            ...(bumpVersion ? { currentVersion: nextVersion } : {}),
            updatedAt: now,
            ownerDisplayName: user.displayName || user.email || 'Anonymous',
            ownerHandle: user.slug || '',
            ownerAvatarUrl: user.avatarUrl ?? user.photoURL ?? null,
          });

          if (bumpVersion) {
            const versionRef = doc(deckVersionsCollection(deckId), String(nextVersion));
            tx.set(versionRef, {
              versionNumber: nextVersion,
              changeNote: opts.changeNote || '',
              snapshot: {
                ...serializeDraft(toPersist),
                isValid: validation.isValid,
                validationErrors: validation.errors,
              },
              savedAt: now,
              savedBy: user.uid,
            });
          }
        });
      } catch (err) {
        if (err instanceof DeckPublishedDuringSave) {
          return publishDeck(deckId, { ...draft, status: 'published' }, {
            bumpVersion: opts.bumpVersion,
            changeNote: opts.changeNote,
          });
        }
        throw err;
      }

      if (bumpVersion) {
        const oldVersion = nextVersion - USER_LIMITS.MAX_VERSIONS_PER_DECK;
        if (oldVersion > 0) {
          const oldRef = doc(deckVersionsCollection(deckId), String(oldVersion));
          deleteDoc(oldRef).catch(() => {}); // fire-and-forget
        }
      }

      const nextTeam = teamFieldsForSave(toPersist, user.teamId);
      await clearFeaturedPointersForDeck({
        deckId,
        ownerHandle: user.slug,
        teamId:
          typeof prevTeamId === 'string' ? prevTeamId : nextTeam.teamId,
      });

      if (bumpVersion) {
        trackEvent(AnalyticsEvents.DECK_SAVE);
      }
      return { saved: true };
    },
    [user, publishDeck],
  );

  const saveDeck = useCallback(
    async (
      deckId: string,
      draft: DeckDraft,
      changeNote?: string,
    ): Promise<{ saved: boolean }> => {
      return persistDeckDraft(deckId, draft, { bumpVersion: true, changeNote });
    },
    [persistDeckDraft],
  );

  const deleteDeck = useCallback(
    async (deckId: string): Promise<void> => {
      if (!user) throw new Error('Must be signed in.');

      const deckSnap = await getDoc(deckDoc(deckId));
      const deckData = deckSnap.exists()
        ? (deckSnap.data() as Record<string, unknown>)
        : null;

      // Batch delete subcollections first, then the deck document.
      // Firestore does not auto-cascade on delete.
      const batch = writeBatch(db);

      const versionsSnap = await getDocs(deckVersionsCollection(deckId));
      versionsSnap.docs.forEach((d) => batch.delete(d.ref));

      const likesSnap = await getDocs(deckLikesCollection(deckId));
      likesSnap.docs.forEach((d) => batch.delete(d.ref));

      batch.delete(deckDoc(deckId));
      await batch.commit();

      if (deckData) {
        await clearFeaturedPointersForDeck({
          deckId,
          ownerHandle:
            typeof deckData.ownerHandle === 'string' ? deckData.ownerHandle : user.slug,
          teamId: typeof deckData.teamId === 'string' ? deckData.teamId : null,
        });
      }

      trackEvent(AnalyticsEvents.DECK_DELETE);
    },
    [user]
  );

  const duplicateDeck = useCallback(
    async (sourceDeckId: string, newTitle?: string): Promise<string> => {
      if (!user) throw new Error('Must be signed in.');

      const snap = await getDoc(deckDoc(sourceDeckId));
      if (!snap.exists()) throw new Error('Source deck not found.');

      const data = snap.data() as Record<string, unknown>;
      const draft = draftFromFirestore(data);
      draft.title = newTitle || `Copy of ${draft.title || 'Untitled'}`;
      draft.status = 'draft';
      draft.visibility = 'private';

      return createDeck(draft, { analytics: 'duplicate' });
    },
    [user, createDeck]
  );

  const updateDeckVisibility = useCallback(
    async (deckId: string, visibility: DeckVisibility): Promise<void> => {
      if (!user) throw new Error('Must be signed in.');

      const nextVisibility: DeckVisibility =
        visibility === 'team' && !user.teamId ? 'private' : visibility;

      const deckSnap = await getDoc(deckDoc(deckId));
      const prevTeamId = deckSnap.exists()
        ? (deckSnap.data() as Record<string, unknown>).teamId
        : null;
      const teamFields = teamFieldsForSave(
        { visibility: nextVisibility } as DeckDraft,
        user.teamId
      );

      const data = deckSnap.exists() ? (deckSnap.data() as Record<string, unknown>) : null;
      if (data?.status === 'published') {
        const currentDraft = draftFromFirestore(data);
        await publishDeck(deckId, {
          ...currentDraft,
          status: 'published',
          visibility: nextVisibility,
        });
        return;
      }

      await updateDoc(deckDoc(deckId), {
        visibility: nextVisibility,
        ...teamFields,
        updatedAt: Timestamp.now(),
      });

      // Clear featured pointers when the deck leaves a surface it was eligible for.
      const ownerHandle = deckSnap.exists()
        ? ((deckSnap.data() as Record<string, unknown>).ownerHandle as string | undefined)
        : user.slug;
      await clearFeaturedPointersForDeck({
        deckId,
        ownerHandle: ownerHandle || user.slug,
        teamId:
          typeof prevTeamId === 'string'
            ? prevTeamId
            : typeof teamFields.teamId === 'string'
              ? teamFields.teamId
              : user.teamId,
        nextVisibility,
      });
    },
    [user, publishDeck]
  );

  return {
    createDeck,
    publishDeck,
    saveDeck,
    persistDeckDraft,
    deleteDeck,
    duplicateDeck,
    updateDeckVisibility,
  };
}

// ---------- public deck listing ----------

type PublicDecksQuery = {
  domain?: string;
  playstyle?: string;
  formatCode?: string;
  hasGuide?: boolean;
  limit?: number;
};

/**
 * In-memory stale-while-revalidate cache for the public deck directory. Keyed
 * by query options so the gallery can re-mount (e.g. after popping back from a
 * deck detail) and render the previous results instantly instead of flashing a
 * spinner while Firestore re-fetches.
 */
const publicDecksCache = new Map<string, Deck[]>();

function publicDecksCacheKey(opts?: PublicDecksQuery): string {
  return JSON.stringify({
    domain: opts?.domain ?? null,
    playstyle: opts?.playstyle ?? null,
    formatCode: opts?.formatCode ?? null,
    hasGuide: opts?.hasGuide ?? null,
    limit: opts?.limit ?? null,
  });
}

/** Synchronously read the last fetched results for a query, if any. */
export function getCachedPublicDecks(opts?: PublicDecksQuery): Deck[] | undefined {
  return publicDecksCache.get(publicDecksCacheKey(opts));
}

export async function fetchPublicDecks(opts?: PublicDecksQuery): Promise<Deck[]> {
  const constraints = [where('visibility', '==', 'public'), where('status', '==', 'published')];

  if (opts?.formatCode) {
    constraints.push(where('formatCode', '==', opts.formatCode));
  }

  const snap = await getDocs(query(decksCollection(), ...constraints));
  let decks: Deck[] = snap.docs.map((d) =>
    deckFromFirestore(d.id, d.data() as Record<string, unknown>)
  );

  // Client-side filters for fields not supported in compound Firestore queries without indexes
  if (opts?.domain) {
    decks = decks.filter((d) => {
      const legendDomains = d.legend?.domain ?? [];
      return legendDomains.includes(opts.domain!);
    });
  }
  if (opts?.playstyle) {
    decks = decks.filter((d) => d.playstyle.includes(opts.playstyle as Deck['playstyle'][number]));
  }
  if (opts?.hasGuide) {
    decks = decks.filter((d) => d.guide && d.guide.trim().length > 0);
  }

  decks.sort((a, b) => compareDecksByGallerySort(a, b, DEFAULT_DECK_GALLERY_SORT));
  const result = opts?.limit ? decks.slice(0, opts.limit) : decks;
  publicDecksCache.set(publicDecksCacheKey(opts), result);
  rememberDecks(result);
  return result;
}

// ---------- owner handle rename ----------

/**
 * Updates `ownerHandle` on all decks belonging to `uid` when the user renames
 * their slug.  Firestore batch writes are capped at 500 ops; split into chunks
 * if the user ever has that many decks.
 */
export async function renameDeckOwnerHandle(uid: string, newHandle: string): Promise<void> {
  const snap = await getDocs(query(decksCollection(), where('ownerId', '==', uid)));
  if (snap.empty) return;

  const handle = newHandle.trim().toLowerCase();
  const chunks: typeof snap.docs[] = [];
  for (let i = 0; i < snap.docs.length; i += 500) {
    chunks.push(snap.docs.slice(i, i + 500));
  }

  for (const chunk of chunks) {
    const batch = writeBatch(db);
    for (const d of chunk) {
      batch.update(d.ref, { ownerHandle: handle });
    }
    await batch.commit();
  }
}

// ---------- public decks by owner ----------

/**
 * Fetch all public published decks for a specific owner handle.
 * Uses the same ordering as `fetchPublicDecks` (newest first).
 */
export async function fetchPublicDecksByOwner(ownerHandle: string): Promise<Deck[]> {
  if (!ownerHandle.trim()) return [];

  const snap = await getDocs(
    query(
      decksCollection(),
      where('visibility', '==', 'public'),
      where('status', '==', 'published'),
      where('ownerHandle', '==', ownerHandle.trim().toLowerCase()),
    )
  );

  const decks: Deck[] = snap.docs.map((d) =>
    deckFromFirestore(d.id, d.data() as Record<string, unknown>)
  );

  decks.sort((a, b) => compareDecksByGallerySort(a, b, DEFAULT_DECK_GALLERY_SORT));
  return decks;
}

// ---------- serialization ----------

/** Firestore forbids `undefined` in document fields (including nested maps). */
function stripUndefinedDeep(value: unknown): unknown {
  if (value === undefined) return undefined;
  if (value === null) return null;
  if (typeof value !== 'object') return value;
  if (Array.isArray(value)) {
    return value.map(stripUndefinedDeep);
  }
  const obj = value as Record<string, unknown>;
  const out: Record<string, unknown> = {};
  for (const k of Object.keys(obj)) {
    const v = stripUndefinedDeep(obj[k]);
    if (v !== undefined) out[k] = v;
  }
  return out;
}

function serializeDraft(draft: DeckDraft): Record<string, unknown> {
  const payload = {
    title: draft.title,
    description: draft.description,
    guide: draft.guide,
    video: draft.video ?? null,
    metafy: draft.metafy ?? null,
    playstyle: draft.playstyle,
    formatCode: draft.formatCode,
    tags: draft.tags,
    coverCardId: draft.coverCardId ?? null,
    matchups: draft.matchups,
    legend: draft.legend ?? null,
    chosenLegends: draft.chosenLegends ?? [],
    champion: draft.champion ?? null,
    battlefields: draft.battlefields,
    runes: draft.runes,
    mainDeck: draft.mainDeck,
    sideboard: draft.sideboard,
    considered: draft.considered,
    status: draft.status,
    visibility: draft.visibility,
  };
  return stripUndefinedDeep(payload) as Record<string, unknown>;
}

// ---------- like / unlike ----------

const LIKE_DEBOUNCE_MS = 500;

/** Session cache so gallery show/hide does not refetch one like doc per deck. */
const deckLikeCache = new Map<string, boolean>();

function deckLikeCacheKey(uid: string, deckId: string): string {
  return `${uid}:${deckId}`;
}

export function useDeckLike(deckId: string) {
  const { user } = useAuth();
  const uid = user?.uid ?? null;
  const cachedLiked = uid ? deckLikeCache.get(deckLikeCacheKey(uid, deckId)) : undefined;
  const [liked, setLiked] = useState(() => cachedLiked ?? false);
  const [loading, setLoading] = useState(false);

  // Refs let debounce callbacks always see fresh values without stale closures.
  const likeRefRef    = useRef(uid ? doc(deckLikesCollection(deckId), uid) : null);
  const serverLiked   = useRef(cachedLiked ?? false); // last state confirmed by Firestore
  const pendingLiked  = useRef(cachedLiked ?? false); // current optimistic intent
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Keep likeRefRef in sync with user / deckId changes.
  likeRefRef.current = uid ? doc(deckLikesCollection(deckId), uid) : null;

  // Seed liked state from the session cache, otherwise Firestore, on mount / user change.
  useEffect(() => {
    const ref = likeRefRef.current;
    if (!ref || !uid) {
      serverLiked.current = false;
      pendingLiked.current = false;
      setLiked((current) => (current ? false : current));
      return;
    }

    const cacheKey = deckLikeCacheKey(uid, deckId);
    const cached = deckLikeCache.get(cacheKey);
    if (cached !== undefined) {
      if (serverLiked.current === cached && pendingLiked.current === cached) return;
      serverLiked.current = cached;
      pendingLiked.current = cached;
      setLiked(cached);
      return;
    }

    let cancelled = false;
    getDoc(ref).then((snap) => {
      // A toggle already recorded a newer intent for this deck.
      if (deckLikeCache.has(cacheKey)) return;
      const exists = snap.exists();
      deckLikeCache.set(cacheKey, exists);
      if (cancelled) return;
      serverLiked.current = exists;
      pendingLiked.current = exists;
      setLiked(exists);
    }).catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [uid, deckId]);

  // Cancel any pending debounce on unmount.
  useEffect(() => () => { if (debounceTimer.current) clearTimeout(debounceTimer.current); }, []);

  const toggle = useCallback(() => {
    if (!user || !likeRefRef.current) return;

    // Optimistic UI flip.
    const newLiked = !pendingLiked.current;
    pendingLiked.current = newLiked;
    if (uid) deckLikeCache.set(deckLikeCacheKey(uid, deckId), newLiked);
    setLiked(newLiked);

    // Reset the debounce window — only the final state gets written.
    if (debounceTimer.current) clearTimeout(debounceTimer.current);

    debounceTimer.current = setTimeout(async () => {
      const finalLiked = pendingLiked.current;
      const confirmedLiked = serverLiked.current;

      // Net-zero: user toggled an even number of times — nothing to write.
      if (finalLiked === confirmedLiked) return;

      const ref = likeRefRef.current;
      if (!ref) return;

      setLoading(true);
      try {
        const batch = writeBatch(db);
        if (finalLiked) {
          batch.set(ref, { likedAt: Timestamp.now() });
          batch.update(deckDoc(deckId), { likeCount: increment(1) });
        } else {
          batch.delete(ref);
          batch.update(deckDoc(deckId), { likeCount: increment(-1) });
        }
        await batch.commit();
        serverLiked.current = finalLiked;
        if (uid) deckLikeCache.set(deckLikeCacheKey(uid, deckId), finalLiked);
      } catch {
        // Revert UI to the last confirmed server state.
        serverLiked.current = confirmedLiked;
        pendingLiked.current = confirmedLiked;
        if (uid) deckLikeCache.set(deckLikeCacheKey(uid, deckId), confirmedLiked);
        setLiked(confirmedLiked);
      } finally {
        setLoading(false);
      }
    }, LIKE_DEBOUNCE_MS);
  }, [user, uid, deckId]);

  return { liked, loading, toggle };
}
